<template>
  <div
    class="responsive-video"
    :class="$blockClass(blockClass, 'responsive-video')"
    :style="`background-image:url(${fallbackImage}); aspect-ratio: ${videoRatio}`"
  >
    <video
      ref="video"
      :src="videoSrc"
      :data-src="videoSrc"
      :type="`video/${videoType}`"
      preload="auto"
      class="responsive-video__video"
      :class="$blockClass(blockClass, 'responsive-video__video')"
      :autoplay="autoplayEnabled"
      loop
      muted
      oncontextmenu="return false;"
      playsinline
    />

    <div
      class="responsive-video__content container"
      :class="[
        $blockClass(blockClass, 'responsive-video-content'),
        { 'has-overlay': hasOverlay }
      ]"
    >
      <div
        v-if="showControls"
        class="responsive-video__controls"
        :class="[
          $blockClass(blockClass, 'responsive-video-controls'),
          { 'responsive-video__controls--reverse-alignment' : reverseControlsAlignment }
        ]"
      >
        <button
          type="button"
          class="responsive-video__controls--sound"
          :aria-label="$string('accessibility.video.sound')"
          @click="toggleSound"
        >
          <icon-sound-off v-if="!isSoundOn" />
          <icon-sound-on v-else />
        </button>

        <button
          type="button"
          :aria-label="$string('accessibility.video.controls')"
          @click="togglePlay"
        >
          <icon-pause v-if="!isPaused" />
          <icon-play v-else />
        </button>
      </div>
      <slot />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Responsive video (responsive-video)
 * -----------------------------------------------------------------------------
 * A video component with options.
 *
* @param {Boolean} autoplayEnabled - Video autoplay property.
* @param {String} [blockClass] - Block level class to apply to all elements.
* @param {String} fallbackImage - Video fallback image for loading state.
* @param {Boolean} hasOverlay - To add an overlay to controls and slot.
* @param {String} ratio - Specific ratio value.
* @param {Boolean} reverseControlsAlignment - Reverse alignment of controls.
* @param {Boolean} showControls - To show video controls.
* @param {String} videoSrc - Video src.
 *
 */
import IconPause from '~icons/misc/pause.svg'
import IconPlay from '~icons/misc/play.svg'
import IconSoundOn from '~icons/misc/sound-on.svg'
import IconSoundOff from '~icons/misc/sound-off.svg'
import { isEmptyString } from '~/helpers/general'

export default {
  name: 'ResponsiveVideo',

  components: { IconPause, IconPlay, IconSoundOff, IconSoundOn },

  props: {
    autoplayEnabled: {
      type: Boolean,
      default: true,
    },
    blockClass: {
      type: String,
      default: '',
    },
    hasOverlay: {
      type: Boolean,
      default: false,
    },
    fallbackImage: {
      type: String,
      default: '',
    },
    ratio: {
      type: String,
      default: '',
    },
    reverseControlsAlignment: {
      type: Boolean,
      default: false,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    videoSrc: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isPaused: false,
      isSoundOn: false,
    }
  },

  computed: {

    /*
    * Returns video type.
    */
    videoType() {
      return this.videoSrc?.split('.').pop().split('?').shift() || 'mp4'
    },

    /*
    * Returns video aspect ratio.
    */
    videoRatio() {
      return isEmptyString(this.ratio) ? '' : this.ratio.replace(':', '/')
    },
  },

  mounted() {
    this.initialiseVideo()
  },

  methods: {

    /*
    * Toggle video mute property.
    */
    toggleSound() {
      this.isSoundOn = !this.isSoundOn
      this.$refs.video.muted = !this.isSoundOn
    },

    /*
    * Toggle video play property.
    */
    togglePlay() {
      if (!this.isPaused) {
        this.$refs.video.pause()
        this.isPaused = true

        return
      }

      this.$refs.video.play()
      this.isPaused = false
    },

    /**
     * Initialise video.
     */
    initialiseVideo() {

      /**
       * Prevent video autoplay if prefers reduced motion, otherwise use prop.
       */
      const autoplay = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? false
        : this.autoplayEnabled

      if (!autoplay) {
        this.isPaused = true
        return
      }

      this.$refs.video.play()
    },
  },
}
</script>
